// Approvisionnements (D1, D2 ; charte D-038, « exceptions d'abord ») : en tête les cargaisons reportées, les
// arrivages en retard et le navire en déchargement ; puis le tableau à 7, 15, 30, 60 et 90 jours comparé aux
// besoins estimés (les volumes annoncés ne sont jamais présentés comme reçus), la programmation (création,
// modification tracée avec motif, historique) et les cargaisons. Les droits viennent du serveur.

import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { api, messageErreur } from '../../api/client';
import { Indisponible, Message, MessageErreur, Squelette } from '../../composants/Etats';
import { EchelleArrivages } from '../../composants/Visualisations';
import { Icone } from '../../composants/Icones';
import { date, dateHeure, jourLong } from '../../composants/format';
import { decimalSaisi, localVersIso, quantite, useNomProduit } from './composants';
import type { Cargaison, Programme, TableauArrivages } from './types';

const MODES: Record<string, string> = { navire: 'Navire', raffinage_local: 'Raffinage local', route: 'Route', autre: 'Autre' };

export function PageApprovisionnements() {
  const nomProduit = useNomProduit();
  const [produit, setProduit] = useState('');
  const [creation, setCreation] = useState(false);
  const tableau = useQuery({
    queryKey: ['approvisionnements', 'tableau', produit],
    queryFn: async () => (await api<TableauArrivages>(`/api/v1/approvisionnements/tableau${produit ? `?produit=${produit}` : ''}`)).donnees,
  });
  const droits = tableau.data?.droits;
  const programmes = useQuery({
    queryKey: ['approvisionnements', 'programmes', produit],
    queryFn: async () => (await api<Programme[]>(`/api/v1/approvisionnements/programmes${produit ? `?produit=${produit}` : ''}`)).donnees,
    enabled: Boolean(droits?.lireProgrammes),
  });
  const cargaisons = useQuery({
    queryKey: ['approvisionnements', 'cargaisons'],
    queryFn: async () => (await api<Cargaison[]>('/api/v1/approvisionnements/cargaisons')).donnees,
    enabled: Boolean(droits?.lireProgrammes),
  });
  if (tableau.isLoading) return <Squelette lignes={8} libelle="Chargement des approvisionnements" />;
  if (tableau.error) return <MessageErreur erreur={tableau.error} titre="Approvisionnements indisponibles" />;
  const t = tableau.data!;
  // Stock et solde ne sont lisibles qu'avec les agrégats nationaux : sans eux, colonnes et mentions masquées (exceptions d'abord).
  const avecStock = t.tableau.some((l) => l.stock !== null && l.stock !== undefined);
  const avecSolde = t.tableau.some((l) => l.horizons.some((h) => h.solde !== null));
  const avecArrivages = t.tableau.filter((l) => l.horizons.some((h) => Number(h.attendu) + Number(h.enRetard) > 0));
  const referenceLigne = (l: (typeof t.tableau)[number]) => String(Math.max(Math.abs(Number(l.stock ?? 0)), ...l.horizons.map((h) => Math.abs(Number(h.attendu)) + Math.abs(Number(h.enRetard)))));
  const enCours = (cargaisons.data ?? []).filter((c) => !['reception_terminee', 'controle_en_cours', 'cloture', 'annule'].includes(c.statutCargaison));
  const reportees = enCours.filter((c) => c.joursReport > 0);
  const dechargement = enCours.filter((c) => c.statutCargaison === 'en_dechargement');
  const retards = (programmes.data ?? []).filter((p) => p.enRetard && !p.cargaisonId);
  const produits = [...new Set(t.tableau.map((l) => l.produit))];

  return (
    <div className="page-approvisionnements">
      <div className="page-tete">
        <div>
          <span className="contexte">{jourLong()}</span>
          <h1>Approvisionnements</h1>
          <p>Arrivages attendus par horizon, comparés aux besoins estimés. Un volume annoncé n’est jamais compté comme reçu.</p>
        </div>
        <div className="actions">
          <div className="champ champ-etroit">
            <label htmlFor="a-produit">Produit</label>
            <select id="a-produit" value={produit} onChange={(e) => setProduit(e.target.value)}>
              <option value="">Tous</option>
              {produits.map((p) => (
                <option key={p} value={p}>
                  {nomProduit(p)}
                </option>
              ))}
            </select>
          </div>
          {droits?.creerProgrammes && (
            <button type="button" className="bouton primaire" onClick={() => setCreation((v) => !v)}>
              <Icone nom="ajout" />
              Programmer un arrivage
            </button>
          )}
        </div>
      </div>

      {creation && <FormulaireProgramme fermer={() => setCreation(false)} />}

      {reportees.length + dechargement.length + retards.length > 0 && (
        <section aria-labelledby="titre-a-traiter-appro" className="espace-bas">
          <h2 id="titre-a-traiter-appro" className="sur-titre">
            À traiter d’abord
          </h2>
          <ol className="liste-exceptions">
            {reportees.map((c, i) => (
              <li key={c.id}>
                <Link className="exception ambre" to={`/approvisionnements/cargaisons/${c.id}`}>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">Cargaison reportée de {c.joursReport} jour{c.joursReport > 1 ? 's' : ''}</span>
                    <b>
                      {c.navire}, {c.produitNom}, {quantite(c.quantiteAnnoncee, c.unite)}
                    </b>
                    <small>
                      Prévue le {date(c.datePrevue)}, arrivée actualisée au {date(c.dateActualisee)}, {c.receptionNom}
                    </small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
            {dechargement.map((c, i) => (
              <li key={c.id}>
                <Link className="exception neutre" to={`/approvisionnements/cargaisons/${c.id}`}>
                  <span className="numero" aria-hidden="true">
                    {reportees.length + i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">En déchargement</span>
                    <b>
                      {c.navire}, {c.produitNom}, {quantite(c.quantiteAnnoncee, c.unite)} annoncés
                    </b>
                    <small>Début le {dateHeure(c.debutDechargement)} : la quantité réceptionnée sera saisie en fin de déchargement</small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
            {retards.slice(0, 5).map((p, i) => (
              <li key={p.id}>
                <span className="exception ambre">
                  <span className="numero" aria-hidden="true">
                    {reportees.length + dechargement.length + i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">Arrivage en retard</span>
                    <b>
                      {p.reference}, {nomProduit(p.produitCode)}, {quantite(p.quantite, p.unite)}
                    </b>
                    <small>
                      Date actualisée dépassée ({date(p.dateActualisee)}) sans réception, {p.destinationNom ?? 'destination non précisée'}
                    </small>
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="carte-bloc" aria-labelledby="titre-horizons">
        <div className="carte-bloc-tete">
          <h2 id="titre-horizons">Arrivages attendus par horizon</h2>
          <span className="attenue">{t.lignes} ligne{t.lignes > 1 ? 's' : ''} de programmation lisibles</span>
        </div>
        {t.tableau.length === 0 ? (
          <Indisponible motif="aucune programmation ni stock de référence dans votre périmètre" />
        ) : (
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Arrivages attendus à 7, 15, 30, 60 et 90 jours comparés aux besoins</caption>
              <thead>
                <tr>
                  <th scope="col">Produit</th>
                  {avecStock && (
                    <th scope="col" className="nombre masquable">
                      Stock actuel
                    </th>
                  )}
                  {t.tableau[0]!.horizons.map((h) => (
                    <th key={h.horizonJours} scope="col" className="nombre">
                      {h.horizonJours} jours
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.tableau.map((l) => (
                  <tr key={`${l.produit}${l.unite}`}>
                    <th scope="row">
                      {l.produitNom}
                      {(avecSolde || l.consommationJournaliere) && <small>{l.consommationJournaliere ? `Besoin estimé ${quantite(l.consommationJournaliere, l.unite, referenceLigne(l))} par jour` : 'Besoin indisponible'}</small>}
                      {Object.keys(l.parEtat).length > 0 && (
                        <small>
                          {Object.entries(l.parEtat)
                            .filter(([e]) => e !== 'annule')
                            .map(([e, q]) => `${(t.libellesEtats[e] ?? e).toLowerCase()} ${quantite(q, l.unite, referenceLigne(l))}`)
                            .join(', ')}
                        </small>
                      )}
                    </th>
                    {avecStock && <td className="nombre masquable">{l.stock ? quantite(l.stock, l.unite) : <span className="cellule-indisponible">indisponible</span>}</td>}
                    {l.horizons.map((h) => {
                      const deficit = h.solde !== null && Number(h.solde) < 0;
                      return (
                        <td key={h.horizonJours} className={`nombre${deficit ? ' negatif' : ''}`}>
                          {Number(h.attendu) === 0 && Number(h.enRetard) === 0 ? <span className="horizon-attendu attenue">aucun</span> : <span className="horizon-attendu">{quantite(h.attendu, l.unite, referenceLigne(l))}</span>}
                          {Number(h.enRetard) > 0 && <small>plus en retard {quantite(h.enRetard, l.unite, referenceLigne(l))}</small>}
                          {avecSolde && <small>{h.solde === null ? 'solde indisponible' : `solde ${quantite(h.solde, l.unite, referenceLigne(l))}`}</small>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Échelles : seulement les produits qui ont un arrivage attendu (exceptions d'abord). */}
        {avecArrivages.length > 0 && (
          <div className="grille-cartes espace-haut">
            {avecArrivages.map((l) => (
              <div key={`echelle-${l.produit}`}>
                <span className="libelle">Échelle d’arrivages, {l.produitNom}</span>
                <EchelleArrivages
                  paliers={l.horizons.map((h) => ({
                    horizon: `${h.horizonJours} j`,
                    volume: l.unite === 'L' ? String((Number(h.attendu) + Number(h.enRetard)) / 1000) : String(Number(h.attendu) + Number(h.enRetard)),
                    retard: l.unite === 'L' ? String(Number(h.enRetard) / 1000) : h.enRetard,
                    unite: l.unite === 'L' ? 'm3' : 'kg',
                  }))}
                  motifIndisponible="aucun arrivage programmé"
                />
              </div>
            ))}
          </div>
        )}
        <p className="legende-tableau">
          Attendu : volumes commandés, annoncés, expédiés ou arrivés dont la date actualisée tombe dans l’horizon. Solde : stock actuel plus attendu moins besoin. {t.sourceBesoins}. {t.baseStock}.
        </p>
      </section>

      {droits?.lireProgrammes && (
        <section className="carte-bloc" aria-labelledby="titre-cargaisons">
          <div className="carte-bloc-tete">
            <h2 id="titre-cargaisons">Cargaisons</h2>
            <span className="attenue">Position des navires saisie manuellement ; connecteur AIS non raccordé</span>
          </div>
          {cargaisons.isLoading && <Squelette lignes={3} />}
          {cargaisons.error && <MessageErreur erreur={cargaisons.error} />}
          {cargaisons.data && cargaisons.data.length === 0 && <p className="attenue">Aucune cargaison dans votre périmètre.</p>}
          {cargaisons.data && cargaisons.data.length > 0 && (
            <div className="tableau-conteneur">
              <table className="tableau">
                <caption className="visuellement-cache">Cargaisons</caption>
                <thead>
                  <tr>
                    <th scope="col">Navire</th>
                    <th scope="col">Statut</th>
                    <th scope="col">Arrivée actualisée</th>
                    <th scope="col" className="nombre">
                      Quantité
                    </th>
                    <th scope="col" className="masquable">
                      Réception
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cargaisons.data.map((c) => (
                    <tr key={c.id} className={c.joursReport > 0 && enCours.includes(c) ? 'exception-ligne' : undefined}>
                      <td>
                        <Link className="lien-ligne" to={`/approvisionnements/cargaisons/${c.id}`}>
                          {c.navire}
                        </Link>
                        <small>
                          {c.reference}, {c.produitNom}, {c.proprietaireNom}
                        </small>
                      </td>
                      <td>
                        <span className={`etiquette${c.joursReport > 0 && enCours.includes(c) ? ' ambre' : ''}`}>{c.statutLibelle}</span>
                      </td>
                      <td>
                        {date(c.dateActualisee)}
                        {c.joursReport > 0 && <small>reportée de {c.joursReport} jour{c.joursReport > 1 ? 's' : ''}</small>}
                      </td>
                      <td className="nombre">
                        {quantite(c.quantiteReceptionnee ?? c.quantiteAnnoncee, c.unite)}
                        <small>{c.quantiteReceptionnee ? 'réceptionnée' : 'annoncée'}</small>
                      </td>
                      <td className="masquable">{c.receptionNom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {droits?.lireProgrammes && <ListeProgrammes programmes={programmes.data ?? null} chargement={programmes.isLoading} erreur={programmes.error} modifiable={Boolean(droits?.modifierProgrammes)} />}
      {!droits?.lireProgrammes && <p className="attenue">Le détail de la programmation et des cargaisons par opérateur n’est pas lisible avec vos habilitations ; le tableau ci-dessus en donne l’agrégat.</p>}
    </div>
  );
}

function ListeProgrammes({ programmes, chargement, erreur, modifiable }: { programmes: Programme[] | null; chargement: boolean; erreur: unknown; modifiable: boolean }) {
  const nomProduit = useNomProduit();
  const [vue, setVue] = useState<'attendus' | 'tous'>('attendus');
  const [choisi, setChoisi] = useState<string | null>(null);
  const [limite, setLimite] = useState(25);
  const lignes = (programmes ?? []).filter((p) => vue === 'tous' || ['commande', 'annonce', 'expedie', 'arrive'].includes(p.etat));
  const selection = lignes.find((p) => p.id === choisi) ?? null;
  return (
    <section className="carte-bloc" aria-labelledby="titre-programmation">
      <div className="carte-bloc-tete">
        <h2 id="titre-programmation">Programmation</h2>
        <div className="segments" role="tablist" aria-label="Lignes affichées">
          <button type="button" role="tab" aria-selected={vue === 'attendus'} onClick={() => setVue('attendus')}>
            Attendus
          </button>
          <button type="button" role="tab" aria-selected={vue === 'tous'} onClick={() => setVue('tous')}>
            Toutes les lignes
          </button>
        </div>
      </div>
      {chargement && <Squelette lignes={4} />}
      {Boolean(erreur) && <MessageErreur erreur={erreur} />}
      {selection && <DetailProgramme programme={selection} modifiable={modifiable} fermer={() => setChoisi(null)} />}
      {programmes && (
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Lignes de programmation</caption>
            <thead>
              <tr>
                <th scope="col">Date actualisée</th>
                <th scope="col">Produit</th>
                <th scope="col" className="nombre">
                  Quantité
                </th>
                <th scope="col">État</th>
                <th scope="col" className="masquable">
                  Provenance, destination
                </th>
              </tr>
            </thead>
            <tbody>
              {lignes.slice(0, limite).map((p) => (
                <tr key={p.id} aria-selected={p.id === choisi} className={p.enRetard ? 'exception-ligne' : undefined}>
                  <td>
                    <button type="button" className="bouton-ligne" onClick={() => setChoisi(p.id === choisi ? null : p.id)}>
                      {date(p.dateActualisee)}
                      <small>
                        {p.reference}
                        {p.dateConfirmee ? ', date confirmée' : ', date estimée'}
                      </small>
                    </button>
                  </td>
                  <td>{nomProduit(p.produitCode)}</td>
                  <td className="nombre">{quantite(p.quantite, p.unite)}</td>
                  <td>
                    <span className={`etiquette${p.enRetard ? ' ambre' : ''}`}>{p.enRetard ? 'En retard' : (LIBELLES_ETATS_PROG[p.etat] ?? p.etat)}</span>
                  </td>
                  <td className="masquable">
                    {MODES[p.mode] ?? p.mode}, {p.provenance}
                    <small>{p.destinationNom ?? 'destination non précisée'}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {programmes && lignes.length > limite && (
        <p className="espace-haut">
          <button type="button" className="bouton secondaire" onClick={() => setLimite((n) => n + 50)}>
            Afficher {Math.min(50, lignes.length - limite)} ligne{Math.min(50, lignes.length - limite) > 1 ? 's' : ''} de plus ({formaterNombre(lignes.length - limite)} non affichée{lignes.length - limite > 1 ? 's' : ''})
          </button>
        </p>
      )}
    </section>
  );
}

const LIBELLES_ETATS_PROG: Record<string, string> = { commande: 'Commandé', annonce: 'Annoncé', expedie: 'Expédié', arrive: 'Arrivé', receptionne: 'Réceptionné', utilisable: 'Utilisable', annule: 'Annulé' };

function DetailProgramme({ programme, modifiable, fermer }: { programme: Programme; modifiable: boolean; fermer: () => void }) {
  const client = useQueryClient();
  const historique = useQuery({
    queryKey: ['approvisionnements', 'historique', programme.id],
    queryFn: async () => (await api<{ changements: { id: string; champ: string; ancienneValeur: string | null; nouvelleValeur: string | null; motif: string; creeLe: string; auteur: { prenom: string; nom: string } | null }[] }>(`/api/v1/approvisionnements/programmes/${programme.id}/historique`)).donnees,
  });
  const [dateConfirmee, setDateConfirmee] = useState(programme.dateConfirmee ? programme.dateConfirmee.slice(0, 10) : '');
  const [etat, setEtat] = useState(programme.etat);
  const [motif, setMotif] = useState('');
  const modification = useMutation({
    mutationFn: async () =>
      api(`/api/v1/approvisionnements/programmes/${programme.id}`, {
        methode: 'PATCH',
        corps: { etat, dateConfirmee: dateConfirmee ? `${dateConfirmee}T12:00:00+01:00` : null, motif },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['approvisionnements'] });
      setMotif('');
    },
  });
  const lieeACargaison = Boolean(programme.cargaisonId);
  return (
    <div className="panneau-saisie">
      <h2>
        {programme.reference}, {quantite(programme.quantite, programme.unite)}
      </h2>
      <p className="attenue">
        {MODES[programme.mode] ?? programme.mode}, {programme.provenance}, fournisseur {programme.fournisseur}, déclaré par {programme.organisationNom ?? 'organisation indisponible'}, version {programme.version}
      </p>
      {lieeACargaison && (
        <p>
          Ligne liée à une cargaison : l’état et la date suivent la <Link to={`/approvisionnements/cargaisons/${programme.cargaisonId}`}>fiche de la cargaison</Link>.
        </p>
      )}
      {modifiable && !lieeACargaison && !['utilisable', 'annule'].includes(programme.etat) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            modification.mutate();
          }}
        >
          <div className="formulaire-ligne">
            <div className="champ">
              <label htmlFor="p-etat">État</label>
              <select id="p-etat" value={etat} onChange={(e) => setEtat(e.target.value)}>
                {Object.entries(LIBELLES_ETATS_PROG).map(([c, l]) => (
                  <option key={c} value={c}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ">
              <label htmlFor="p-date">Date confirmée</label>
              <input id="p-date" type="date" value={dateConfirmee} onChange={(e) => setDateConfirmee(e.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor="p-motif">Motif du changement (obligatoire)</label>
              <input id="p-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} />
            </div>
          </div>
          {modification.error && <Message type="erreur">{messageErreur(modification.error)}</Message>}
          {modification.isSuccess && <Message type="succes">Changement enregistré et tracé.</Message>}
          <div className="actions">
            <button type="submit" className="bouton primaire" disabled={modification.isPending}>
              Enregistrer le changement
            </button>
          </div>
        </form>
      )}
      <h3 className="espace-haut">Changements tracés</h3>
      {historique.data && historique.data.changements.length === 0 && <p className="attenue">Aucun changement depuis la création.</p>}
      {historique.data && historique.data.changements.length > 0 && (
        <ol className="chronologie">
          {historique.data.changements.map((c) => (
            <li key={c.id}>
              {c.champ} : {c.ancienneValeur ?? 'vide'} devient {c.nouvelleValeur ?? 'vide'}
              <small>
                {dateHeure(c.creeLe)}, {c.auteur ? `${c.auteur.prenom} ${c.auteur.nom}` : 'système'}, motif : {c.motif}
              </small>
            </li>
          ))}
        </ol>
      )}
      <div className="actions espace-haut">
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </div>
  );
}

function FormulaireProgramme({ fermer }: { fermer: () => void }) {
  const client = useQueryClient();
  const nomProduit = useNomProduit();
  const destinations = useQuery({ queryKey: ['stocks', 'installations', 'depots'], queryFn: async () => (await api<{ id: string; nom: string; categorie: string }[]>('/api/v1/stocks/installations')).donnees });
  const produits = ['gasoil', 'super', 'petrole_lampant', 'jet_a1', 'fuel_lourd', 'gpl_butane'];
  const [d, setD] = useState({ produitCode: 'gasoil', quantite: '', provenance: '', fournisseur: '', mode: 'raffinage_local', installationDestinationId: '', dateEstimee: '' });
  const envoi = useMutation({
    mutationFn: async () =>
      api('/api/v1/approvisionnements/programmes', {
        corps: { ...d, quantite: decimalSaisi(d.quantite), installationDestinationId: d.installationDestinationId || null, dateEstimee: localVersIso(`${d.dateEstimee}T08:00`) },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['approvisionnements'] });
      fermer();
    },
  });
  const maj = (cle: keyof typeof d) => (e: { target: { value: string } }) => setD({ ...d, [cle]: e.target.value });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
      aria-labelledby="titre-nouveau-programme"
    >
      <h2 id="titre-nouveau-programme">Programmer un arrivage</h2>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="n-produit">Produit</label>
          <select id="n-produit" value={d.produitCode} onChange={maj('produitCode')}>
            {produits.map((p) => (
              <option key={p} value={p}>
                {nomProduit(p)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="n-quantite">Quantité (unité du produit)</label>
          <input id="n-quantite" inputMode="decimal" value={d.quantite} onChange={maj('quantite')} required />
        </div>
        <div className="champ">
          <label htmlFor="n-mode">Mode</label>
          <select id="n-mode" value={d.mode} onChange={maj('mode')}>
            {Object.entries(MODES).map(([c, l]) => (
              <option key={c} value={c}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="n-date">Date estimée d’arrivée</label>
          <input id="n-date" type="date" value={d.dateEstimee} onChange={maj('dateEstimee')} required />
        </div>
      </div>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="n-provenance">Provenance</label>
          <input id="n-provenance" value={d.provenance} onChange={maj('provenance')} required minLength={2} />
        </div>
        <div className="champ">
          <label htmlFor="n-fournisseur">Fournisseur</label>
          <input id="n-fournisseur" value={d.fournisseur} onChange={maj('fournisseur')} required minLength={2} />
        </div>
        <div className="champ">
          <label htmlFor="n-destination">Destination</label>
          <select id="n-destination" value={d.installationDestinationId} onChange={maj('installationDestinationId')}>
            <option value="">Non précisée</option>
            {(destinations.data ?? [])
              .filter((x) => x.categorie !== 'station')
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.nom}
                </option>
              ))}
          </select>
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
          Enregistrer la programmation
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Annuler
        </button>
      </div>
    </form>
  );
}
